import { MediumText } from '@/lib/Text';
import { Col } from '@/lib/utils';
import { BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar } from '@mustmove/recharts';
import dayjs from 'dayjs';
import React, { useState } from 'react';
import { CustomBar } from './CustomBar';
import { CustomTooltip } from './CustomTooltip';
import { FeedDashboardMonthlyMetricChange } from '../../services/useFeedDashboardQueryService';

type Props = {
    monthlyMetricChanges?: FeedDashboardMonthlyMetricChange[];
    month: string;
};

const FeedCenterStatisticsPageGraph = ({ monthlyMetricChanges, month }: Props) => {
    const [hoverState, setHoverState] = useState<{
        index: number | null;
        dataKey: string | null;
    }>({ index: null, dataKey: null });
    const [mouseYRef, setMouseYRef] = useState(0);

    return (
        <Col className="flex w-full bg-[#FFFFFF] px-[20px] py-[25px] rounded-[21px]  h-[532px]">
            <MediumText fontSize={18} lineHeight={23} color={'#4B545F'} className="mb-[14px]">
                최근 4개월 지표 변화
            </MediumText>
            <ResponsiveContainer width={'100%'} height={'100%'}>
                <BarChart
                    data={monthlyMetricChanges ?? []}
                    barCategoryGap="20%"
                    barGap={20}
                    margin={{ top: 50, bottom: 40 }} // ✅ bottom 여백 추가
                    onMouseMove={(state, e) => {
                        if (e) {
                            const nativeEvent = e.nativeEvent as any;

                            // mouseYRef.current = nativeEvent.layerY; // 마우스 Y 좌표 업데이트
                            setMouseYRef(nativeEvent.layerY); // 마우스 Y 좌표 업데이트

                            setHoverState((prev: any) => {
                                const nextIndex = state.activeTooltipIndex;
                                const nextKey = state.activeDataKey;

                                // Tooltip이 꺼졌거나, 데이터 없음
                                if (nextIndex === undefined || nextKey === null) return prev;

                                // ✅ index 또는 dataKey 둘 중 하나만 달라도 갱신
                                if (prev.index !== nextIndex || prev.dataKey !== nextKey) {
                                    return { index: nextIndex, dataKey: nextKey };
                                }
                                return prev;
                            });
                        }
                    }}
                    onMouseLeave={() => {
                        setHoverState({ index: null, dataKey: null });
                        // mouseYRef.current = 0; // 마우스 Y 좌표 초기화
                        setMouseYRef(0); // 마우스 Y 좌표 초기화
                    }}
                >
                    <CartesianGrid
                        stroke="#E5E5E5" // 선 색상
                        vertical={false} // 세로 선은 비활성화 (원하면 true로)
                    />
                    <XAxis
                        dataKey="monthString"
                        tickLine={false}
                        axisLine={true}
                        interval={0}
                        tick={({ x, y, payload }) => {
                            const _month = Number(String(payload?.value)?.replace('월', '')?.trim()) || 0;
                            const isCurrentMonth = dayjs(month).month() + 1 === _month;

                            const textHeight = 24;
                            const circleSize = 28;

                            return (
                                <g transform={`translate(${x + 3},${y + 8})`}>
                                    {isCurrentMonth && (
                                        <rect
                                            x={-circleSize / 2}
                                            y={0}
                                            width={circleSize}
                                            height={circleSize}
                                            rx={circleSize / 2}
                                            ry={circleSize / 2}
                                            fill="#000"
                                        />
                                    )}
                                    <text
                                        x={0}
                                        y={0}
                                        dy={textHeight / 2 + 6}
                                        textAnchor="middle"
                                        fill={isCurrentMonth ? '#fff' : '#4B545F'}
                                        fontSize={12}
                                        fontWeight={400}
                                        letterSpacing={-0.5}
                                    >
                                        {payload.value}
                                    </text>
                                </g>
                            );
                        }}
                        stroke="#E5E5E5"
                    />

                    <YAxis
                        domain={[0, 100]}
                        ticks={[0, 25, 50, 75, 100]}
                        tickFormatter={(value) => `${value}%`}
                        axisLine={false}
                        tickLine={false}
                        width={50}
                    />
                    <Tooltip
                        content={(props) => {
                            return <CustomTooltip {...props} mouseYRef={mouseYRef} />;
                        }}
                        cursor={false}
                        shared={false}
                        isAnimationActive={false}
                    />

                    <Bar
                        dataKey="centerReportWriteRate"
                        fill="#8884d8"
                        className="custom-class"
                        shape={(props: any) => {
                            return (
                                <CustomBar
                                    month={month}
                                    {...props}
                                    hoverState={hoverState}
                                    dataKey="centerReportWriteRate"
                                    fill="#5088FF"
                                />
                            );
                        }}
                    />
                    <Bar
                        dataKey="centerReRegistrationRate"
                        fill="#020202"
                        shape={(props: any) => (
                            <CustomBar
                                month={month}
                                {...props}
                                hoverState={hoverState}
                                dataKey="centerReRegistrationRate"
                                fill="#C170FF"
                            />
                        )}
                    />
                    <Bar
                        dataKey="centerReRegistrationSuccessRate"
                        fill="#169873"
                        shape={(props: any) => (
                            <CustomBar
                                month={month}
                                {...props}
                                hoverState={hoverState}
                                dataKey="centerReRegistrationSuccessRate"
                                fill="#169873"
                            />
                        )}
                    />
                </BarChart>
            </ResponsiveContainer>
        </Col>
    );
};

export default FeedCenterStatisticsPageGraph;
